import ContentConfig from "@/react/story/components/ContentConfig";
import ContentText from "@/react/story/components/ContentText";
import UiBtnGroup from "@/react/story/components/ui/UiBtnGroup";
import UiColorPicker from "@/react/story/components/ui/UiColorPicker";
import UiDropzone from "@/react/story/components/ui/UiDropzone";
import UiSeparator from "@/react/story/components/ui/UiSeparator";
import { CONTENT_TYPES, HFLEXS, PADDINGS, VFLEXS } from "@/react/story/data";
import { type Block, type Config, type Content, ContentTypeEnum, type Separator, TypeEnum } from "@/react/story/types";
import InitUtils from "@/react/story/utils/InitUtils";
import { useEffect, useState } from "react";
import { ReactSortable } from "react-sortablejs";

type Props = {
    block: Block;
    saveBlock: (block: Block) => void;
    deleteBlock?: () => void;
}

export default function PageBlock(props: Props) {
    const [ opened, setOpened ] = useState<Content | null>(null);

    const setSeparator = (separator: Separator | null): void => {
        props.saveBlock({ ...props.block, separator });
    };

    const addContent = (type: ContentTypeEnum): void => {
        const content: Content = {
            "#type": TypeEnum.CONTENT,
            uid: InitUtils.uid(),
            type,
            config: InitUtils.config(),
        };
        setOpened(content);
        props.saveBlock({ ...props.block, contents: [ ...props.block.contents, content ] });
    };

    const saveContent = (content: Content) => {
        setOpened(content);
        const contents = props.block.contents.map(c => c.uid === content.uid ? content : c);
        if (!props.block.contents.some(c => c.uid === content.uid)) {
            contents.push(content);
        }
        props.saveBlock({ ...props.block, contents });
    };

    const deleteContent = (content: Content) => {
        setOpened(null);
        props.saveBlock({ ...props.block, contents: props.block.contents.filter(c => c.uid !== content.uid) });
    };

    const setContents = (contents: Content[]) => {
        const order1 = props.block.contents.map(c => c.uid).join("/");
        const order2 = contents.map(c => c.uid).join("/");
        if (order1 !== order2) {
            props.saveBlock({ ...props.block, contents });
        }
    };

    const setConfig = (config: Partial<Config>) => {
        props.saveBlock({ ...props.block, config: { ...props.block.config, ...config } });
    };

    useEffect(() => {
        setOpened(null);
    }, []);

    useEffect(() => {
        if (opened) {
            props.block.contents.forEach(c => {
                if (c.uid === opened.uid) {
                    setOpened(c);
                }
            });
        }
    }, [ props.block ]);

    if (opened) {
        return (
            <ContentConfig
                content={opened}
                viewContent={setOpened}
                saveContent={saveContent}
                deleteContent={deleteContent}
            />
        );
    }

    return (
        <div className="d-flex flex-column gap-5">
            {props.deleteBlock ? (
                <div className="d-flex justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => props.deleteBlock?.()}
                    >
                        Supprimer
                    </button>
                </div>
            ) : null}
            <div>
                <h3 className="text-uppercase fs-4">Arrière plan</h3>
                <div className="d-flex flex-column gap-3">
                    <div>
                        <strong className="d-block">Couleur</strong>
                        <UiColorPicker
                            color={props.block.config.backgroundColor}
                            onChange={backgroundColor => setConfig({ backgroundColor })}
                        />
                    </div>
                    <div>
                        <strong className="d-block">Image</strong>
                        <UiDropzone
                            defaultValue={props.block.config.backgroundImage}
                            canDelete={true}
                            onChange={backgroundImage => setConfig({ backgroundImage })}
                        />
                    </div>
                </div>
            </div>
            <div>
                <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                    <h3 className="text-uppercase fs-4 mb-0">Contenus</h3>
                    <div className="dropdown">
                        <button className="btn btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <span className="bi-plus"></span>
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end">
                            {CONTENT_TYPES.map((type, i) => (
                                <li key={i}>
                                    <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => addContent(type.value)}
                                    >
                                        {type.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="d-flex flex-column gap-3">
                    <>
                        {/* @ts-expect-error react-sortablejs types incompatible with React 18 */}
                        <ReactSortable list={props.block.contents} setList={setContents} className="d-flex flex-column gap-3">
                            {props.block.contents.map((content, i) => (
                                <ContentText
                                    key={i}
                                    content={content}
                                    viewContent={setOpened}
                                />
                            ))}
                        </ReactSortable>
                    </>
                </div>
            </div>
            <div>
                <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                    <h3 className="text-uppercase fs-4 mb-0">Image secondaire</h3>
                    <button
                        type="button"
                        className={`btn btn-outline-${props.block.separator ? "danger" : "secondary"}`}
                        onClick={() => setSeparator(props.block.separator ? null : {
                            "#type": TypeEnum.SEPARATOR,
                            uid: InitUtils.uid(),
                            config: InitUtils.config(),
                        })}
                    >
                        <span className={`bi-${props.block.separator ? "dash" : "plus"}`}></span>
                    </button>
                </div>
                {props.block.separator ? (
                    <UiSeparator
                        separator={props.block.separator}
                        saveSeparator={setSeparator}
                    />
                ) : null}
            </div>
            <div>
                <h3 className="text-uppercase fs-4">Positions</h3>
                <div className="d-flex flex-column gap-3">
                    <div>
                        <strong className="d-block">Alignement horizontal</strong>
                        <UiBtnGroup
                            items={HFLEXS}
                            isActive={v => v === props.block.config.alignItems || (HFLEXS[0].value === v && !props.block.config.alignItems)}
                            set={alignItems => setConfig({ alignItems })}
                        />
                    </div>
                    <div>
                        <strong className="d-block">Alignement vertical</strong>
                        <UiBtnGroup
                            items={VFLEXS}
                            isActive={v => v === props.block.config.justifyContent || (VFLEXS[0].value === v && !props.block.config.justifyContent)}
                            set={justifyContent => setConfig({ justifyContent })}
                        />
                    </div>
                    <div>
                        <strong className="d-block">Marges au bord du contenu</strong>
                        <UiBtnGroup
                            items={PADDINGS}
                            isActive={v => v === props.block.config.p || (PADDINGS[0].value === v && !props.block.config.p)}
                            set={p => setConfig({ p })}
                        />
                    </div>
                    <div>
                        <strong className="d-block">Marge entre les éléments</strong>
                        <UiBtnGroup
                            items={PADDINGS}
                            isActive={v => v === props.block.config.gap || (PADDINGS[0].value === v && !props.block.config.gap)}
                            set={gap => setConfig({ gap })}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
